import React, { useState } from 'react'

import { DEFAULT_ONRAMP_FIAT_CURRENCY, DEFAULT_ONRAMP_TOKEN } from '@src/pages/FiatExchange/config'
import { FiatCurrency } from '@src/types/fiat'
import { IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils'
import { useConfigStore } from '@src/zustand_stores'

import FiatSelectionPill from './FiatSelectionPill'
import TokenSelectionPill from './TokenSelectionPill'

const logger = createContextLogger('BuySection')

export interface BuySectionProps {
    /** Value to display */
    value: string | number
    /** Placeholder text for the input; defaults to '0' */
    placeholder?: string
    /** Callback to notify parent of token changes (for onramp) */
    onBuyTokenChange?: (newToken: IToken) => void
    /** Callback to notify parent of currency changes (for offramp) */
    onBuyCurrencyChange?: (newCurrency: FiatCurrency) => void
    /** Whether a quote is currently being fetched */
    isLoading?: boolean
    /** Whether this is in offramp mode (buy fiat instead of token) */
    isOfframp?: boolean
}

const BuySection: React.FC<BuySectionProps> = ({
    value,
    placeholder = '0',
    onBuyTokenChange,
    onBuyCurrencyChange,
    isLoading = false,
    isOfframp = false,
}) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)

    // Initialize state based on the mode
    const [selectedToken, setSelectedToken] = useState<IToken>(DEFAULT_ONRAMP_TOKEN)
    const [selectedFiat, setSelectedFiat] = useState<FiatCurrency>(DEFAULT_ONRAMP_FIAT_CURRENCY)

    const handleTokenChange = (newData: { chainId?: number; token?: IToken }) => {
        if (newData.token && onBuyTokenChange) {
            setSelectedToken(newData.token)
            onBuyTokenChange(newData.token)
            logger.info('Onramp token changed to', newData.token)
        } else {
            logger.warn('No token provided in token change')
        }
    }

    // When a new fiat is selected (for offramp), update local state and notify parent
    const handleFiatChange = (fiat?: FiatCurrency) => {
        if (fiat && onBuyCurrencyChange) {
            setSelectedFiat(fiat)
            onBuyCurrencyChange(fiat)
            logger.info('Offramp fiat currency changed', fiat)
        }
    }

    return (
        <div className='space-y-4 rounded-3xl border-mach-surface-border bg-mach-surface-background p-5'>
            {/* Clear floats so that this row starts below the floated element */}
            <div className='clear-both flex items-center justify-between'>
                {/* Label row */}
                <div className='flex items-center gap-2'>
                    <span className='text-sm text-mach-accent-forgeround'>Buy</span>
                </div>

                {isOfframp ? (
                    <FiatSelectionPill fiat={selectedFiat} onChange={handleFiatChange} />
                ) : (
                    <TokenSelectionPill
                        onChange={handleTokenChange}
                        token={selectedToken}
                        availableNetworks={availableNetworks}
                    />
                )}
            </div>

            <div className='flex items-center justify-between'>
                {isLoading ? (
                    <div className='w-full text-left text-lg text-gray-400 md:text-[32px]'>Loading...</div>
                ) : (
                    <input
                        className='w-full bg-transparent text-left text-lg md:text-[32px]'
                        placeholder={placeholder}
                        value={value}
                        disabled
                        inputMode='decimal'
                        required
                    />
                )}
                <div className='relative'>{/* Additional UI can go here */}</div>
            </div>
        </div>
    )
}

export default BuySection
